import { type FieldResponseDTO } from '@capsicum/common';
import { Divider, Skeleton, SkeletonItem, Text } from '@capsicum/common-web';
import { useTransactionDetails } from '@capsicum/features/wallet/shared';
import { useParams } from 'next/navigation';
import React from 'react';
import styled, { css } from 'styled-components';

import { useOrderCancellation } from '../../hooks';
import { TransactionCard, TransactionSectionHeader } from '../../ui';

import { valueMap } from './valueMap';

const containerStyle = css`
	padding: 0;
	height: auto;
`;

const subTitleStyle = css`
	margin: 0;
`;

const cardStyle = css`
	margin: 0;
	cursor: default;
`;

const CARD_HEIGHT = 80;
const HEADER_HEIGHT = 51;

const renderValueByDataType = (field: FieldResponseDTO) => {
	const Component = valueMap[field.dataType!];

	if (Component && typeof Component !== 'string') {
		return <Component data={field.data} isCopyable={field.isCopyable} />;
	}

	return Component;
};

export const TransactionDetails = () => {
	const { transactionId } = useParams<{ transactionId: string }>();
	const { isLoading, partnerLogoUrl, sections, transaction } = useTransactionDetails({ transactionId });
	const orderCancellation = useOrderCancellation(transactionId);

	if (isLoading) {
		return (
			<S.SkeletonContainer>
				<Skeleton borderRadius={4}>
					<S.SkeletonItemStyled height={HEADER_HEIGHT} />
					<S.SkeletonItemStyled count={8} height={CARD_HEIGHT} />
				</Skeleton>
			</S.SkeletonContainer>
		);
	}

	return (
		<S.Container>
			<TransactionCard
				cardStyle={cardStyle}
				{...transaction}
				orderCancellation={orderCancellation}
				partnerLogoUrl={partnerLogoUrl}
			/>
			{sections?.map((section, index) => (
				<S.SectionContainer key={index}>
					<TransactionSectionHeader
						containerStyle={containerStyle}
						leftText={section.title as string}
						subTitleStyle={subTitleStyle}
					/>
					<S.ContainerGroup>
						{section.rows?.map((row, index) => (
							<React.Fragment key={index}>
								{index > 0 && <Divider orientation="horizontal" />}
								{row.fields?.map((field, index) => (
									<S.FieldContainer key={index}>
										<S.TextFiled variant="text3">{field.label}</S.TextFiled>
										<Text variant="text1">{renderValueByDataType(field)}</Text>
									</S.FieldContainer>
								))}
							</React.Fragment>
						))}
					</S.ContainerGroup>
				</S.SectionContainer>
			))}
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		gap: ${({ theme }) => theme.spacings.xxlarge};
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		padding: ${({ theme }) => `${theme.spacings.large} ${theme.spacings.small} ${theme.spacings.small}`};
	`;

	export const ContainerGroup = styled.div`
		padding: ${({ theme }) => theme.spacings.small};
		${({ theme }) => theme.border.radius.medium};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const SectionContainer = styled.div`
		gap: ${({ theme }) => theme.spacings.xsmall};
		display: flex;
		flex-direction: column;
	`;

	export const TextFiled = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const FieldContainer = styled.div`
		display: flex;
		justify-content: space-between;
		margin: ${({ theme }) => theme.spacings.xsmall} 0;
		min-height: 24px;

		&:first-child {
			margin-top: 0;
		}

		&:last-child {
			margin-bottom: 0;
		}
	`;

	export const SkeletonContainer = styled.div`
		padding: ${({ theme }) => theme.spacings.small};
		width: 100%;
	`;

	export const SkeletonItemStyled = styled(SkeletonItem)`
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;
}
